import { cloneDeep } from "es-toolkit"
import { defineStore } from "pinia"
import { ref, computed, toRaw, type Ref } from "vue"
import updateModifier from "~/utils/update/updateModifier"
import type { Modifier } from "~/types"

export const useModifiersGlobalStore = defineStore(
  "modifiersGlobal",
  () => {
    // State
    // This provides a reserved range of 1-999 for preset modifiers initially.
    const modifierGlobalIdNext: Ref<number> = ref(1000)
    const modifiersGlobal: Ref<Modifier[]> = ref([])
    const modifiersGlobalSelected: Ref<string[]> = ref([])
    // Getters
    const globalModifiersCount = computed(() => {
      return {
        active: modifiersGlobalSelected.value.length,
        total: modifiersGlobal.value.length,
      }
    })

    const globalModifiersFilteredBySelected = computed(() => {
      const returnArray: Modifier[] = []
      modifiersGlobalSelected.value.forEach((selectedId) => {
        modifiersGlobal.value.forEach((modifier) => {
          if (modifier.id === selectedId) {
            returnArray.push(toRaw(modifier))
          }
        })
      })
      return returnArray
    })
    // Actions
    function deleteGlobalModifier(payload: Modifier) {
      const modifiersGlobalIndex = modifiersGlobal.value.indexOf(payload)
      const modifiersGlobalSelectedIndex =
        modifiersGlobalSelected.value.indexOf(payload.id)
      modifiersGlobal.value.splice(modifiersGlobalIndex, 1)
      modifiersGlobalSelected.value.splice(modifiersGlobalSelectedIndex, 1)
    }
    function initModifiersGlobal() {
      modifiersGlobal.value = []
      modifierGlobalIdNext.value = 1
      modifiersGlobalSelected.value = []
    }
    function updateModifiersGlobal(
      options: {
        applyAllUpdates?: boolean
        applyLatestUpdate?: boolean
        versionToUpdateFrom?: string
      } = {}
    ) {
      // Applies any pending updates to global modifiers.

      const {
        applyAllUpdates = false,
        applyLatestUpdate = false,
        versionToUpdateFrom,
      } = options

      console.log("\nUpdating global modifiers...\n\n")

      // Initialize modifiersGlobal as an empty array if it's undefined
      if (!modifiersGlobal.value) {
        modifiersGlobal.value = []
        console.log("Initialised empty modifiersGlobal array")
        return
      }

      modifiersGlobal.value = modifiersGlobal.value.map((modifier) => {
        return updateModifier(modifier, {
          applyAllUpdates,
          applyLatestUpdate,
          versionToUpdateFrom,
        })
      })
    }
    function writeToModifiersGlobal(payload: Modifier) {
      // Check if payload modifier already exists in modifiersGlobal & modifiersGlobalSelected
      // and get indices.
      let modifier = payload
      const modifiersGlobalIndex = modifiersGlobal.value.findIndex(
        (existingModifier) => existingModifier.id === modifier.id
      )
      const modifiersGlobalSelectedIndex =
        modifiersGlobalSelected.value.findIndex(
          (existingModifierId) => existingModifierId === modifier.id
        )
      if (modifiersGlobalIndex >= 0) {
        // This is an existing modifier. Overwrite at the correct existing index in both arrays.
        modifiersGlobal.value.splice(modifiersGlobalIndex, 1, modifier)
        modifiersGlobalSelected.value.splice(
          modifiersGlobalSelectedIndex,
          1,
          modifier.id
        )
      } else {
        // This is a new modifier.
        // It could be from a preset, or it could be newly created from scratch.
        if (modifier.id === "new") {
          // This is a new modifier from a preset (presets have negative IDs).
          // Clone the modifier, breaking any references to the original payload object.
          modifier = cloneDeep(payload)
          // Assign the clone a new unique ID.
          modifier.id = crypto.randomUUID()
          // Override the scope to be just ["global"].
          // The preset might have listed multiple possible scopes.
          modifier.scope = ["global"]
        }
        // Push to both arrays.
        modifiersGlobal.value.push(modifier)
        modifiersGlobalSelected.value.push(modifier.id)
      }
    }

    return {
      // State
      modifierGlobalIdNext,
      modifiersGlobal,
      modifiersGlobalSelected,
      // Getters
      globalModifiersCount,
      globalModifiersFilteredBySelected,
      // Actions
      deleteGlobalModifier,
      initModifiersGlobal,
      updateModifiersGlobal,
      writeToModifiersGlobal,
    }
  },
  {
    persist: {
      key: "unitcrunch-modifiersGlobal",
      storage: piniaPluginPersistedstate.localStorage(),
      pick: [
        "modifierGlobalIdNext",
        "modifiersGlobal",
        "modifiersGlobalSelected",
      ],
    },
  }
)
